// @flow

import { VALIDATION_RESULT_GROUPINGS } from 'app/developer-v3/components/SidebarRight/Validate/constants';
import {
  ACTION_INPUT_TYPES,
  ACTION_INPUT_TYPE_TEXTS,
  ACTION_TYPES,
  APP_INTENTION,
  BACKEND_APP_INTENTION,
  FORM_TYPES,
  VersionStatus,
} from 'app/developer-v3/constants';

import type { CreateActionSchema } from 'app/developer-v3/platformSchema/create-action';
import type {
  FieldType,
  StaticDropdown,
} from 'app/developer-v3/platformSchema/field';
import type { FunctionSchema } from 'app/developer-v3/platformSchema/function';
import type {
  LegacyCreateActionSchema,
  LegacySearchActionSchema,
  LegacyTriggerSchema,
} from 'app/developer-v3/platformSchema/legacy';
import type { RequestSchema } from 'app/developer-v3/platformSchema/request';
import type { RequestStatusType } from 'app/developer-v3/components/RequestTester/StatusIndicator';
import type { SearchActionSchema } from 'app/developer-v3/platformSchema/search-action';
import type { StringMap } from 'app/common/types';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';
import {
  AppStatus,
  AppStatusName,
  VersionLifecycle,
} from 'app/developer-v3/types/statuses';
import type { SemVer, VersionParams } from 'app/developer-v3/types/version';

export type AppIntentionType = $Values<typeof APP_INTENTION>;

export type BackendAppIntentionType = $Values<typeof BACKEND_APP_INTENTION>;

export type AppType = {|
  id: number,
  image?: string,
  title: string,
  isBeta: boolean,
  isPublic: boolean,
  isPending: boolean,
  status: AppStatus,
  version: SemVer,
  versions: SemVer[],
  selectedApi: string,
|};

export type BackendAppType = {
  id: number,
  title: string,
  is_beta: ?boolean,
  public: boolean,
  pending: boolean,
  key: string,
  latest_version: SemVer,
  versions: SemVer[],
  description: string,
  homepage_url: string,
  image?: string,
  intention: BackendAppIntentionType,
  app_category: string,
  app_category_other?: string,
  role: string,
  subscription?: boolean,
  status?: AppStatusName | number,
};

export type AppDefinitionProps = {
  definition: {},
  onSaveDefinition: (definition: string) => void,
};

export type AppDefinitionState = {
  modifiedDefinition: string,
};

export type PartialDefinition = {
  authentication?: {
    test: RequestSchema,
  },
  triggers?: {
    operation: {
      perform: RequestSchema | FunctionSchema,
      performList?: RequestSchema,
      performSubscribe?: RequestSchema,
      performUnsubscribe?: RequestSchema,
    },
  },
  creates?: {
    operation: {
      perform: RequestSchema,
      performGet?: RequestSchema,
    },
  },
  searches?: {
    operation: {
      perform: RequestSchema,
      performGet?: RequestSchema,
    },
  },
};

export type AppSummary = {
  appId: number,
  definition: PartialDefinition,
  definitionOverride: PartialDefinition,
  imageUrl: string,
  isBeta: boolean,
  isGUIIntegration: boolean,
  isPending: boolean,
  isPrivate: boolean,
  isPublic: boolean,
  isPublished: boolean,
  key: string,
  selectedApi: string,
  status: AppStatusName,
  title: string,
  users: {
    count: number,
    exceededLimit: boolean,
  },
  version: SemVer,
  versionLifecycle: VersionLifecycle,
  versionStatus: VersionStatus,
  versions: SemVer[],
  wasConvertedFromWB: boolean,
};

export type AppVersion = {
  app_id: number,
  // changelog: null,
  core_npm_version: SemVer,
  date: string,
  definition_override: any, // basically a mirror of schema
  deployment: 'non-production' | 'production',
  deprecation_date: string | null,
  environment: StringMap,
  etag: string,
  id: number,
  oauth_redirect_uri?: string,
  platform_version: SemVer,
  selected_api: string,
  user_count: number,
  version: SemVer,
};

export type AppForm = FORM_TYPES;

export type AppFormType = {
  id?: string,
  title: string,
  description: string,
  image?: string,
  imageFile?: {
    preview?: string,
    name?: string,
    type?: string,
    size?: number,
  },
  intention?: AppIntentionType,
  app_category: string,
  app_category_other?: string,
  role: string,
  subscription?: boolean,
};

export type NewAppFormType = {
  title?: string,
  description?: string,
  image?: string,
  imageFile?: {
    preview?: string,
    name?: string,
    type?: string,
    size?: number,
  },
  intention?: AppIntentionType,
  app_category?: string,
  app_category_other?: string,
  role?: string,
  subscription?: boolean,
};

export type ActionInputType = $Values<typeof ACTION_INPUT_TYPES>;
export type ActionInputTypeText = $Values<typeof ACTION_INPUT_TYPE_TEXTS>;

export type ActionType = $Keys<typeof ACTION_TYPES>;

export type IntegrationSection = 'settings' | 'inputDesigner' | 'apiConfig';

export type TriggerParams = {
  ...$Exact<VersionParams>,
  triggerKey: string,
  section: IntegrationSection,
};

export type ActionParams = {
  ...$Exact<VersionParams>,
  actionType: ActionType,
  actionKey: string,
  section: IntegrationSection,
};

export type ActionInputParams = {
  ...$Exact<ActionParams>,
  inputKey: string,
  childInputKey: string,
};

export type TriggerInputParams = {
  ...$Exact<TriggerParams>,
  inputKey: string,
};

export type AuthFieldParams = {
  ...$Exact<VersionParams>,
  fieldKey: string,
};

export type BaseFormField = {
  key: string,
  label: string,
  options?: {},
};

export type TestRequestType = {
  isExecuting: boolean,
  output: {} | Object[],
  status: RequestStatusType,
  errorMessage: string,
  timestampMs: number,
};

export type TestLogsType = {
  http: {
    isLoading: boolean,
    logs: Object[],
  },
  console: {
    isLoading: boolean,
    logs: Object[],
  },
  bundle: {
    isLoading: boolean,
    logs: Object[],
  },
};

export type ObjectType = 'trigger' | 'create' | 'search' | 'authentication';

export type OperationMethodType =
  | 'perform'
  | 'performList'
  | 'performSubscribe'
  | 'performUnsubscribe'
  | 'test';

export type FormifiedDynamicDropdown = {
  source: string,
  name: string,
  label?: string,
};

export type DropdownType = 'choices' | 'dynamic';

export type DropdownConfigs = {
  choices: ?StaticDropdown,
  dynamic: ?FormifiedDynamicDropdown,
};

export type FormFieldTemplate = {
  key: string,
  label: string,
  required?: boolean | ((values: *) => boolean),
  min?: number,
  disallowed?: string[],
  help_text_html?: string,
  fields?: Array<{
    type: string,
    key: string,
    label: string,
  }>,
  readOnly?: boolean,
  choices?: Array<{ label: string, value: string }>,
};

export type FormifiedFieldSchema = {
  key: string,
  label: string,
  helpText: string,
  options?: {
    usesDropdown: boolean,
    required?: boolean,
    list?: boolean,
    altersDynamicFields?: boolean,
  },
  type?: FieldType,
  default?: string,
  dynamic?: {
    source: string,
    name: string,
    label?: string,
  },
  choices?: StaticDropdown,
  source?: string,
  search?: string,
  children?: Array<FormifiedFieldSchema>,
};

export type FormifiedSettings =
  | {
      key: string,
      name: string,
      noun: string,
      description: string,
      directions: string,
      visibility: string,
    }
  | {
      key: string,
      name: string,
      noun: string,
      description: string,
      visibility: string,
    };

export type ActionSchema = CreateActionSchema & SearchActionSchema;
export type LegacyActionSchema = LegacyCreateActionSchema &
  LegacySearchActionSchema;

export type AppService = TriggerSchema | ActionSchema;
export type AppServiceLegacy = LegacyTriggerSchema | LegacyActionSchema;

export type AppDeveloper = {
  country: string,
  createdAt: string,
  dateJoined: string,
  email: string,
  firstName: string,
  id: number,
  lastName: string,
  name: string,
  notifyVia: number,
  shouldNotify: boolean,
  timezone: string,
  username: string,
  zapierUserId: number,
};

export type NotificationSettings = {
  shouldNotifyDeveloper: boolean,
  developer: AppDeveloper,
  ccContact?: AppDeveloper,
};

export type ValidationResultType = $Keys<typeof VALIDATION_RESULT_GROUPINGS>;

export type ValidationFailureResult = {
  tag: string,
  message: string,
  deepLink: string,
};

export type ViolationDocumentation = {
  title: string,
  body: string,
};

export type ValidationViolation = {
  type: string,
  results: Array<ValidationFailureResult>,
};

export type ValidationResultSet = {
  type: ValidationResultType,
  violations: Array<ValidationViolation>,
};

export type ValidationResultSetWrapper = {
  totalFailures: number,
  results: Array<ValidationResultSet>,
};

export type ValidationCheckType = 'read' | 'write' | 'search' | 'auth' | 'meta';

export type ImplementationValidationCheck = {
  type: ValidationCheckType,
  violations: Array<{
    type: string,
    results: Array<{
      message: string,
      tag: string,
    }>,
  }>,
};

export type ViolationTypes = 'activationTasks' | 'errors' | 'warnings' | '';
export type DeepLinkParams = {
  type?: ViolationTypes,
  message?: string,
};

export type QueryHighlight = {
  highlight?: string,
  message?: string,
  type?: string,
};

export type ApiHighlightField = {
  field?: string,
  type?: string,
  message?: string,
};

export type InfoMeta = {
  [key: string]: {
    type: string,
    message: string,
  },
};
